/**
 * Time helpers for the notifications inbox.
 *
 * The viewer's own timezone is the rule (owner decision Q271,
 * `packages/errors/src/dateTime.ts:8-11`), read in the browser as
 * `Intl.DateTimeFormat().resolvedOptions().timeZone`. These helpers stay
 * local to this feature: `@sportsmet/errors` is the one place a display
 * DATE is formatted, but it has no relative-time helper and no
 * day-grouping helper, so those two live here instead.
 */
import { formatDate } from "@sportsmet/errors";

const MINUTE_MS = 60_000;
const HOUR_MS = 3_600_000;
const DAY_MS = 86_400_000;
const WEEK_MS = 604_800_000;

export function resolveViewerTimeZone(): string {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return timeZone === "" ? "America/New_York" : timeZone;
}

export function formatRelativeTime({
  isoInstant,
  now,
}: {
  isoInstant: string;
  now: Date;
}): string {
  const elapsedMs = Math.max(0, now.getTime() - new Date(isoInstant).getTime());

  if (elapsedMs < MINUTE_MS) return "now";
  if (elapsedMs < HOUR_MS) return `${Math.floor(elapsedMs / MINUTE_MS)}m`;
  if (elapsedMs < DAY_MS) return `${Math.floor(elapsedMs / HOUR_MS)}h`;
  if (elapsedMs < WEEK_MS) return `${Math.floor(elapsedMs / DAY_MS)}d`;
  return formatDate(isoInstant, { timeZone: resolveViewerTimeZone() });
}

function dayKey({ date, timeZone }: { date: Date; timeZone: string }): string {
  return new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

export function resolveDayHeader({
  isoInstant,
  now,
  timeZone,
}: {
  isoInstant: string;
  now: Date;
  timeZone: string;
}): string {
  const instant = new Date(isoInstant);
  const yesterday = new Date(now.getTime() - DAY_MS);

  const instantKey = dayKey({ date: instant, timeZone });
  const todayKey = dayKey({ date: now, timeZone });
  const yesterdayKey = dayKey({ date: yesterday, timeZone });

  if (instantKey === todayKey) return "Today";
  if (instantKey === yesterdayKey) return "Yesterday";
  return formatDate(isoInstant, { timeZone });
}

export function groupByDay<T extends { createdAtIso: string }>({
  items,
  now,
  timeZone,
}: {
  items: readonly T[];
  now: Date;
  timeZone: string;
}): { header: string; items: T[] }[] {
  const groups: { header: string; items: T[] }[] = [];

  for (const item of items) {
    const header = resolveDayHeader({
      isoInstant: item.createdAtIso,
      now,
      timeZone,
    });
    const lastGroup = groups[groups.length - 1];
    if (lastGroup?.header === header) {
      lastGroup.items.push(item);
    } else {
      groups.push({ header, items: [item] });
    }
  }

  return groups;
}
